import * as React from "react";
import { format } from "date-fns";
import { Calendar as CalendarIcon, Clock } from "lucide-react";

import { cn } from "../../lib/utils";
import { Button } from "./button";
import { Calendar } from "./calendar";
import { Popover, PopoverContent, PopoverTrigger } from "./popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./select";

interface DateTimePickerProps {
  date?: Date | undefined;
  setDate: (date: Date | undefined) => void;
  onBlur?: () => void;
  onKeyDown?: (e: React.KeyboardEvent) => void;
  autoFocus?: boolean;
}

export function DateTimePicker({
  date,
  setDate,
  onBlur,
  onKeyDown,
  autoFocus = false,
}: DateTimePickerProps) {
  // Simple state tracking
  const [isOpen, setIsOpen] = React.useState(false);
  const [selectedDate, setSelectedDate] = React.useState<Date>(
    date || new Date()
  );

  // Convert to 12-hour format state
  const [hours12, setHours12] = React.useState(() => {
    if (date) {
      const hour24 = date.getHours();
      return hour24 === 0
        ? "12"
        : hour24 > 12
        ? String(hour24 - 12)
        : String(hour24);
    }
    return "12";
  });

  const [minutes, setMinutes] = React.useState(() => {
    if (date) {
      const mins = date.getMinutes();
      // Round to nearest 15-minute increment
      const roundedMins = Math.round(mins / 15) * 15;
      return String(roundedMins === 60 ? 0 : roundedMins).padStart(2, "0");
    }
    return "00";
  });

  const [ampm, setAmpm] = React.useState(() => {
    if (date) {
      return date.getHours() >= 12 ? "PM" : "AM";
    }
    return "AM";
  });

  const buttonRef = React.useRef<HTMLButtonElement>(null);
  const [dateChanged, setDateChanged] = React.useState(false);

  // Refs to track if we've applied changes
  const appliedChangeRef = React.useRef(false);

  // Auto-focus if needed
  React.useEffect(() => {
    if (autoFocus && buttonRef.current) {
      buttonRef.current.focus();
      setTimeout(() => setIsOpen(true), 50);
    }
  }, [autoFocus]);

  // Keep local state synced with prop
  React.useEffect(() => {
    if (date) {
      setSelectedDate(new Date(date));
      const hour24 = date.getHours();
      const hour12 = hour24 === 0 ? 12 : hour24 > 12 ? hour24 - 12 : hour24;
      setHours12(String(hour12));

      // Round minutes to nearest 15-minute increment
      const mins = date.getMinutes();
      const roundedMins = Math.round(mins / 15) * 15;
      setMinutes(String(roundedMins === 60 ? 0 : roundedMins).padStart(2, "0"));

      setAmpm(hour24 >= 12 ? "PM" : "AM");
    } else {
      // Set to current time if no date provided
      const now = new Date();
      setSelectedDate(now);
      const hour24 = now.getHours();
      const hour12 = hour24 === 0 ? 12 : hour24 > 12 ? hour24 - 12 : hour24;
      setHours12(String(hour12));

      // Round minutes to nearest 15-minute increment
      const mins = now.getMinutes();
      const roundedMins = Math.round(mins / 15) * 15;
      setMinutes(String(roundedMins === 60 ? 0 : roundedMins).padStart(2, "0"));

      setAmpm(hour24 >= 12 ? "PM" : "AM");
    }
    // Reset the flags when the prop date changes
    setDateChanged(false);
    appliedChangeRef.current = false;
  }, [date]);

  // Handle calendar selection
  const handleSelect = (day: Date | undefined) => {
    if (!day) return;

    const newDate = new Date(day);

    // Copy time from current selection
    newDate.setHours(
      selectedDate.getHours(),
      selectedDate.getMinutes(),
      selectedDate.getSeconds()
    );

    setSelectedDate(newDate);
    setDateChanged(true);

    // Don't apply the change immediately - wait for Done button or save action
    // This prevents the row from jumping while editing
  };

  // Apply time changes locally (update selectedDate but don't propagate)
  const applyTimeChangeLocally = (
    hours: string,
    mins: string,
    ampmValue: string
  ) => {
    const hour12Val = parseInt(hours, 10);
    const minuteVal = parseInt(mins, 10);

    // Convert 12-hour to 24-hour format
    let hour24 = hour12Val;
    if (ampmValue === "AM" && hour12Val === 12) {
      hour24 = 0;
    } else if (ampmValue === "PM" && hour12Val !== 12) {
      hour24 = hour12Val + 12;
    }

    const updatedDate = new Date(selectedDate);
    let changed = false;

    if (!isNaN(hour24) && hour24 >= 0 && hour24 < 24) {
      updatedDate.setHours(hour24);
      changed = true;
    }

    if (!isNaN(minuteVal) && minuteVal >= 0 && minuteVal < 60) {
      updatedDate.setMinutes(minuteVal);
      changed = true;
    }

    if (changed) {
      setSelectedDate(updatedDate);
    }
  };

  // Apply time changes and return the final date (for saving)
  const applyTimeChange = () => {
    const hour12Val = parseInt(hours12, 10);
    const minuteVal = parseInt(minutes, 10);

    // Convert 12-hour to 24-hour format
    let hour24 = hour12Val;
    if (ampm === "AM" && hour12Val === 12) {
      hour24 = 0;
    } else if (ampm === "PM" && hour12Val !== 12) {
      hour24 = hour12Val + 12;
    }

    const updatedDate = new Date(selectedDate);
    let changed = false;

    if (!isNaN(hour24) && hour24 >= 0 && hour24 < 24) {
      updatedDate.setHours(hour24);
      changed = true;
    }

    if (!isNaN(minuteVal) && minuteVal >= 0 && minuteVal < 60) {
      updatedDate.setMinutes(minuteVal);
      changed = true;
    }

    if (changed) {
      setSelectedDate(updatedDate);
      setDateChanged(true);
    }

    return updatedDate;
  };

  // Handle time component changes - only update local state, don't propagate yet
  const handleHoursChange = (value: string) => {
    setHours12(value);
    setDateChanged(true);
    // Apply time change to local selectedDate but don't propagate yet
    applyTimeChangeLocally(value, minutes, ampm);
  };

  const handleMinutesChange = (value: string) => {
    setMinutes(value);
    setDateChanged(true);
    // Apply time change to local selectedDate but don't propagate yet
    applyTimeChangeLocally(hours12, value, ampm);
  };

  const handleAmPmChange = (value: string) => {
    setAmpm(value);
    setDateChanged(true);
    // Apply time change to local selectedDate but don't propagate yet
    applyTimeChangeLocally(hours12, minutes, value);
  };

  // Save changes
  const saveChanges = () => {
    // Apply the final time change and get the updated date
    const finalDate = applyTimeChange();

    // Always propagate the changes if there were any
    if (dateChanged) {
      setDate(finalDate);
    }

    // Always call onBlur if provided
    if (onBlur) {
      onBlur();
    }
  };

  // Handle done button
  const handleDone = () => {
    // Save and close
    saveChanges();
    setIsOpen(false);
  };

  // Handle key navigation
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Escape") {
      // Cancel changes - restore to original date
      if (date) {
        setSelectedDate(new Date(date));
        const hour24 = date.getHours();
        const hour12 = hour24 === 0 ? 12 : hour24 > 12 ? hour24 - 12 : hour24;
        setHours12(String(hour12));
        const mins = date.getMinutes();
        const roundedMins = Math.round(mins / 15) * 15;
        setMinutes(
          String(roundedMins === 60 ? 0 : roundedMins).padStart(2, "0")
        );
        setAmpm(hour24 >= 12 ? "PM" : "AM");
      }
      setDateChanged(false);
      setIsOpen(false);
      if (onBlur) {
        onBlur();
      }
    } else if (e.key === "Tab" || e.key === "Enter") {
      saveChanges();
      setIsOpen(false);

      if (e.key === "Enter") e.preventDefault();
      if (onKeyDown) {
        onKeyDown(e);
      }
    } else if (onKeyDown) {
      onKeyDown(e);
    }
  };

  // Handle outside click
  const handleOpenChange = (open: boolean) => {
    if (!open && isOpen) {
      // Closing by outside click or escape
      saveChanges();
    }
    setIsOpen(open);
  };

  return (
    <Popover open={isOpen} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          onClick={() => setIsOpen(true)}
          onKeyDown={handleKeyDown}
          className={cn(
            "w-full justify-start text-left font-normal h-9 min-h-9",
            !date && "text-muted-foreground"
          )}
          ref={buttonRef}
        >
          <CalendarIcon className="mr-2 h-4 w-4" />
          {selectedDate ? (
            format(selectedDate, "PPP h:mm a")
          ) : (
            <span>Pick a date</span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0 z-[100]" align="start">
        <Calendar
          mode="single"
          selected={selectedDate}
          onSelect={handleSelect}
          initialFocus
        />
        <div className="p-3 border-t border-border flex items-center justify-between">
          <div className="flex items-center space-x-1.5">
            <Clock className="h-4 w-4 text-muted-foreground" />
            <div className="flex items-center space-x-0.5">
              {/* Hours selector */}
              <Select value={hours12} onValueChange={handleHoursChange}>
                <SelectTrigger className="w-14 h-8 text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Array.from({ length: 12 }, (_, i) => i + 1).map((hour) => (
                    <SelectItem key={hour} value={String(hour)}>
                      {hour}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <span className="text-muted-foreground text-xs">:</span>

              {/* Minutes selector */}
              <Select value={minutes} onValueChange={handleMinutesChange}>
                <SelectTrigger className="w-14 h-8 text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="00">00</SelectItem>
                  <SelectItem value="15">15</SelectItem>
                  <SelectItem value="30">30</SelectItem>
                  <SelectItem value="45">45</SelectItem>
                </SelectContent>
              </Select>

              {/* AM/PM selector */}
              <Select value={ampm} onValueChange={handleAmPmChange}>
                <SelectTrigger className="w-14 h-8 text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="AM">AM</SelectItem>
                  <SelectItem value="PM">PM</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="ml-1">
            <Button size="sm" onClick={handleDone} className="text-xs">
              Done
            </Button>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}
